import { useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useTokens } from '../primitives';

// "You vs competitors": how often the AI engines named this business vs up
// to 3 competitors in the latest recurring AI presence run (doc 28 W12,
// Growth and above). The backend returns null for tenants that are not
// entitled or have no run yet, so this renders nothing for them.

const pct = (r) => `${Math.round((Number(r) || 0) * 100)}%`;

function Row({ label, mentions, rate, total, highlight, color }) {
  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: highlight ? 700 : 500, minWidth: 0 }}
          noWrap
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
        >
          {mentions} of {total} ({pct(rate)})
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.round((Number(rate) || 0) * 100))}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 },
        }}
      />
    </Box>
  );
}

export default function CompetitorBenchmarkCard({ benchmark }) {
  const t = useTokens();
  const [showEngines, setShowEngines] = useState(false);
  if (!benchmark || !Array.isArray(benchmark.competitors)) return null;
  if (!benchmark.competitors.length || !benchmark.answersCompared) return null;

  const total = benchmark.answersCompared;
  const when = benchmark.scoredAt ? new Date(benchmark.scoredAt) : null;
  const checkedOn =
    when && !Number.isNaN(when.getTime())
      ? when.toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        })
      : null;
  const rows = [
    { ...benchmark.you, label: `${benchmark.you.name} (you)`, you: true },
    ...benchmark.competitors.map((c) => ({ ...c, label: c.name })),
  ];
  const providers = Array.isArray(benchmark.providers)
    ? benchmark.providers
    : [];
  const summary =
    benchmark.rank === 1
      ? 'You were named as often as or more than every competitor.'
      : benchmark.leader
        ? `${benchmark.leader.name} is named most often. The fixes below are how you close that gap.`
        : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        You vs competitors in AI answers
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        How often each business was named across {total} AI{' '}
        {total === 1 ? 'answer' : 'answers'} to {benchmark.questionsCompared}{' '}
        {benchmark.questionsCompared === 1 ? 'question' : 'questions'} from
        {checkedOn ? ` your check on ${checkedOn}.` : ' your latest check.'}
        {benchmark.competitorSource === 'ai_answers'
          ? ' Competitors are the businesses the engines recommended most.'
          : ''}
      </Typography>

      <Stack spacing={1.5}>
        {rows.map((r) => (
          <Row
            key={r.label}
            label={r.label}
            mentions={r.mentions}
            rate={r.rate}
            total={total}
            highlight={r.you}
            color={r.you ? t.purple : 'text.disabled'}
          />
        ))}
      </Stack>

      {summary && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          {summary}
        </Typography>
      )}

      {providers.length > 1 && (
        <>
          <Button
            size="small"
            onClick={() => setShowEngines((v) => !v)}
            sx={{ mt: 1.5, px: 0, textTransform: 'none' }}
          >
            {showEngines ? 'Hide engine breakdown' : 'Show engine breakdown'}
          </Button>
          <Collapse in={showEngines} unmountOnExit>
            <Box sx={{ overflowX: 'auto', mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {providers.map((p) => (
                      <TableCell key={p.key} align="right">
                        {p.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.label}>
                      <TableCell
                        sx={{ fontWeight: r.you ? 700 : 400, maxWidth: 180 }}
                      >
                        {r.label}
                      </TableCell>
                      {providers.map((p) => (
                        <TableCell key={p.key} align="right">
                          {pct(r.byProvider?.[p.key]?.rate)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </>
      )}
    </Paper>
  );
}
